export interface PartnerSearchItem {
  id: number;
  playerId: number;
  playerName: string;
  note: string | null;
  createdAt: string;
  pendingOfferCount: number;
  singlesRating: number | null;
  doublesRating: number | null;
}

export interface IncomingOffer {
  id: number;
  status: string;
  message: string | null;
  createdAt: string;
  fromPlayerId: number;
  fromPlayerName: string;
  doublesRating: number | null;
}

export interface OutgoingOffer {
  id: number;
  status: string;
  message: string | null;
  createdAt: string;
  searchId: number;
  toPlayerId: number;
  toPlayerName: string;
  doublesRating: number | null;
}

export interface MyPartnerState {
  canUsePool: boolean;
  poolBlock: string;
  player: { id: number; displayName: string | null } | null;
  mySearch: {
    id: number;
    status: string;
    note: string | null;
    createdAt: string;
    offers: IncomingOffer[];
  } | null;
  myOffers: OutgoingOffer[];
}

// Every reason the API can refuse with, said the way a player would say it. An
// unmapped code falls back to something honest rather than leaking the enum.
const BLOCK_MESSAGES: Record<string, string> = {
  PLAYER_NOT_LINKED:
    'Twoje konto nie jest jeszcze powiązane z zawodnikiem w rankingu. Poproś organizatora, żeby dopisał Cię do ligi — bez tego nie da się zawiązać pary.',
  NOT_A_MEMBER: 'Ta liga jest prywatna — wejdź przez link z kodem od organizatora.',
  ALREADY_SIGNED_UP: 'Jesteś już na liście tej kolejki — także jeśli czekasz na zatwierdzenie.',
  NOT_DOUBLES: 'Ta liga gra singla — partnera się nie szuka.',
  ROUND_NOT_OPEN: 'Zapisy na tę kolejkę są zamknięte.',
  INVALID_CODE: 'Kod dostępu jest nieprawidłowy.',
  NOT_AUTHENTICATED: 'Zaloguj się, żeby korzystać z puli partnerów.',
  SEARCH_NOT_OPEN: 'To zgłoszenie znalazło już parę.',
  OWN_SEARCH: 'To Twoje własne zgłoszenie.',
  OFFER_ALREADY_SENT: 'Już wysłałeś propozycję tej osobie — czeka na odpowiedź.',
  OFFER_DECLINED: 'Ta osoba odrzuciła Twoją propozycję.',
  OFFER_NOT_PENDING: 'Ta propozycja nie czeka już na decyzję.',
  NOT_OWNER: 'O tej propozycji decyduje tylko osoba, która wystawiła zgłoszenie.',
};

export function partnerBlockMessage(block: string): string {
  return BLOCK_MESSAGES[block] ?? 'Nie możesz teraz skorzystać z puli partnerów.';
}

export function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '?';
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

  return (first + last).toUpperCase();
}

// The pool is worth rendering whenever somebody is in it, even after signups
// close — the list is also a record of who was still free.
export function shouldRenderPool(params: { isDoubles: boolean; enabled: boolean; itemCount: number }): boolean {
  if (!params.isDoubles) {
    return false;
  }

  return params.enabled || params.itemCount > 0;
}

export function outgoingOfferLabel(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'Czeka na odpowiedź';

    case 'ACCEPTED':
      return 'Przyjęta — gracie razem';

    case 'DECLINED':
      return 'Odrzucona';

    case 'WITHDRAWN':
      return 'Wycofana';

    case 'SUPERSEDED':
      return 'Nieaktualna — ta osoba znalazła parę';

    default:
      return status;
  }
}
